import { useEffect, useMemo, useState } from 'react';
import { useFilesIfProvided } from '../data/useFiles';
import { noteFiles, noteLibrary } from '../domain/resources';

// The pictures a lesson note shows, resolved for the web's preview (#531).
//
// A note names library files by id (`![Fingering](resource:file_…)`) and
// never holds a URL; core/src/resources.rs says why. This reads the ids out of
// the note, reads each against the library the drawer already holds
// (`noteFiles`, the same reading the email makes at send time), and asks for
// a link to each one still stored through `linkFor` — the same thirty-day
// `fileLinks` link Copy link hands out, held and renewed by
// src/data/fileLinks.js, and asked for in one call for all of them.
//
// What it returns is the renderer's `library` map, or null until the first
// answer. Null is not an error: the renderer draws every library reference as
// its name until it has a link to draw it from, so the preview shows the
// names for a moment and then the pictures. A link that cannot be had —
// offline, with none held — leaves that one as its name; a deleted or swept
// file has no path and is its name for good.
//
// **Re-resolved only when what the note names changes**, not on every
// keystroke: typing a sentence around a picture keeps the map it has. And the
// previous map is kept while a new one is asked for, so adding a second
// picture does not flash the first back to its name.
export default function useNoteLibrary(source) {
  const library = useFilesIfProvided();
  const rows = library?.files || null;
  const linkFor = library?.linkFor || null;

  const files = useMemo(() => (rows ? noteFiles(source, rows) : []), [source, rows]);
  // What the answer depends on, as a value: the ids, names, paths and kinds.
  const key = JSON.stringify(files);

  const [resolved, setResolved] = useState(null);

  useEffect(() => {
    // Read back from `key` rather than closed over: `files` is a new array on
    // every keystroke, and `key` is what changes only when it means something.
    const wanted = JSON.parse(key);
    if (!wanted.length || !linkFor) {
      setResolved(null);
      return undefined;
    }
    let live = true;
    const withLink = (f) => (f.path
      // Two arguments, not a trailing `.catch` (#184): the handler is for the
      // link's own failure, which leaves this file as its name.
      ? linkFor(f.path).then((url) => ({ ...f, url }), () => ({ ...f, url: null }))
      : Promise.resolve({ ...f, url: null }));
    Promise.all(wanted.map(withLink)).then((list) => {
      if (live) setResolved(noteLibrary(list));
    });
    return () => { live = false; };
  }, [key, linkFor]);

  return resolved;
}
